import {Link, useSearchParams} from 'react-router';
import {useContenedores} from '../api/seguimiento';
import {CajaAjustable} from '../componentes/caja-ajustable';
import {CompletarContenedores} from '../componentes/completar-contenedores';
import {usePermisos} from '../componentes/sesion-contexto';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {AYUDA_CONTENEDORES as A} from '../componentes/ayuda-exportaciones';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {tr} from '../i18n/idioma';
import {RangoDeLlegada, RangoDeZarpe} from '../componentes/rango-de-llegada';
import {fecha, numero, textoOGuion} from '../utilidades/formato';

// Seguimiento → Contenedores: lo que se cargo de "Seguimiento Concentrados",
// una fila por contenedor, como el Excel. Cada fila lleva al detalle de su
// exportacion (seccion Logistica por contenedor). Filtros en la direccion.
export function PaginaContenedores() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = useContenedores(parametros);
  const x = consulta.data;
  const permisos = usePermisos();
  const cambiar = (clave: string, valor: string) => {
    const nuevos = new URLSearchParams(parametros);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    if (clave !== 'pagina') nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const pagina = Number(parametros.get('pagina') ?? 1);

  return (
    <section className="space-y-4">
      <PestanasSeguimiento />
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          {tr('Contenedores (logística)', 'Containers (logistics)')}
        </h1>
        <p className="text-sm text-apagado">
          {tr(
            'Lo que trae el archivo de seguimiento de concentrados, un contenedor por fila: fechas del recorrido, inspección de la Policía, selectividad de la DIAN (del GEX), sellos, peso del cargue (ITR) contra el de llegada a la refinería y BL. Abre una exportación para ver el detalle por proveedor y el recorrido completo.',
            'What the concentrates tracking file holds, one container per row: journey dates, Police inspection, DIAN selectivity (from the GEX), seals, loading weight (ITR) against refinery arrival weight, and BL. Open an export to see the detail by supplier and the full journey.',
          )}
        </p>
      </div>
      {permisos.administrar && <CompletarContenedores />}

      <ZonaAmpliable titulo={tr('Contenedores (logística)', 'Containers (logistics)')}>
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-linea bg-white p-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-apagado">
              {tr('Buscar', 'Search')}
              <NotaDeAyuda texto={A.buscar} dentroDeEtiqueta />
            </span>
            <input
              type="search"
              defaultValue={parametros.get('buscar') ?? ''}
              onKeyDown={e => e.key === 'Enter' && cambiar('buscar', e.currentTarget.value.trim())}
              onBlur={e =>
                e.currentTarget.value.trim() !== (parametros.get('buscar') ?? '') &&
                cambiar('buscar', e.currentTarget.value.trim())
              }
              placeholder={tr('Exportación o contenedor', 'Export or container')}
              className="w-56 rounded-md border border-linea px-2 py-1"
            />
          </label>
          <SelectorMultiple
            etiqueta={tr('Año de zarpe', 'Departure year')}
            valor={parametros.get('anio') ?? undefined}
            opciones={(x?.opciones.anios ?? []).map(a => ({valor: String(a), etiqueta: String(a)}))}
            onCambiar={v => cambiar('anio', v ?? '')}
            ayuda={A.anio}
            clase="flex w-40 flex-col gap-1 text-xs text-apagado"
          />
          <RangoDeZarpe parametros={parametros} cambiar={cambiar} />
          <RangoDeLlegada parametros={parametros} cambiar={cambiar} />
          <label className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              checked={parametros.get('soloParaRevisar') === 'true'}
              onChange={e => cambiar('soloParaRevisar', e.target.checked ? 'true' : '')}
            />
            {tr('Solo para revisar', 'Only to review')}
            <NotaDeAyuda texto={A.soloParaRevisar} dentroDeEtiqueta />
          </label>
          {x && (
            <span className="ml-auto text-apagado">
              {tr(`${numero(x.total)} contenedores`, `${numero(x.total)} containers`)}
            </span>
          )}
        </div>

        {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
        {x && (
          <CajaAjustable className="overflow-auto rounded-lg border border-linea bg-white">
            <table className="w-full text-sm" data-prueba="contenedores">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                  <th className="px-3 py-2 font-semibold">
                    {tr('Exportación', 'Export')}
                    <NotaDeAyuda texto={A.exportacion} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Contenedor', 'Container')}
                    <NotaDeAyuda texto={A.contenedor} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Tipo', 'Type')}
                    <NotaDeAyuda texto={A.tipo} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Big Bags
                    <NotaDeAyuda texto={A.bigBags} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Proveedores', 'Suppliers')}
                    <NotaDeAyuda texto={A.proveedores} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Recogido en planta', 'Picked up')}
                    <NotaDeAyuda texto={A.recogido} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Entró al puerto', 'Port entry')}
                    <NotaDeAyuda texto={A.entradaPuerto} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Zarpe', 'Departure')}
                    <NotaDeAyuda texto={A.zarpe} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Llegó a la refinería', 'Refinery arrival')}
                    <NotaDeAyuda texto={A.llegadaRefineria} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Inspección Policía', 'Police inspection')}
                    <NotaDeAyuda texto={A.inspeccionPolicia} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Selectividad DIAN', 'DIAN selectivity')}
                    <NotaDeAyuda texto={A.selectividadDian} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {tr('Sellos', 'Seals')}
                    <NotaDeAyuda texto={A.sellos} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {tr('ITR (kg)', 'ITR (kg)')}
                    <NotaDeAyuda texto={A.itr} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {tr('Refinería (kg)', 'Refinery (kg)')}
                    <NotaDeAyuda texto={A.refineriaKg} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {tr('Diferencia (kg)', 'Difference (kg)')}
                    <NotaDeAyuda texto={A.diferencia} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    BL
                    <NotaDeAyuda texto={A.bl} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {x.elementos.map(c => (
                  <tr key={`${c.exportacion}-${c.contenedor}`} className="border-t border-linea/70 align-top">
                    <td className="px-3 py-1.5">
                      <Link
                        to={`/seguimiento/${encodeURIComponent(c.exportacion)}#logistica`}
                        className="font-medium text-verde hover:underline"
                      >
                        {c.exportacion}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5">
                      {c.contenedor}
                      {c.paraRevisar && <span className="ml-1 text-xs">{tr('⚠ revisar', '⚠ review')}</span>}
                    </td>
                    <td className="px-3 py-1.5">{textoOGuion(c.tipo)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{numero(c.bigBags)}</td>
                    <td className="px-3 py-1.5">{textoOGuion(c.proveedores)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fecha(c.recoleccionPlanta)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fecha(c.entradaPuerto)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fecha(c.zarpe)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fecha(c.llegadaRefineria)}</td>
                    <td className="px-3 py-1.5">
                      {textoOGuion(c.inspeccionPolicia)}
                      {c.fechaInspeccion && <span className="text-apagado"> {fecha(c.fechaInspeccion)}</span>}
                    </td>
                    <td className="px-3 py-1.5">{textoOGuion(c.selectividadDian)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{numero(c.sellos)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{numero(c.pesoItrKg)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{numero(c.pesoRefineriaKg)}</td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                      {c.diferenciaKg === null ? '—' : `${c.diferenciaKg > 0 ? '+' : ''}${numero(c.diferenciaKg)}`}
                    </td>
                    <td className="px-3 py-1.5">{textoOGuion(c.bl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {x.elementos.length === 0 && (
              <p className="p-4 text-sm text-apagado">
                {tr(
                  'No hay contenedores con estos filtros. Si la lista está vacía, falta cargar el archivo de seguimiento de concentrados.',
                  'No containers with these filters. If the list is empty, the concentrates tracking file has not been loaded yet.',
                )}
              </p>
            )}
          </CajaAjustable>
        )}

        {x && x.totalPaginas > 1 && (
          <div className="flex items-center justify-end gap-3 text-sm">
            <button
              type="button"
              disabled={pagina <= 1}
              onClick={() => cambiar('pagina', String(pagina - 1))}
              className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
            >
              {tr('← Anterior', '← Previous')}
            </button>
            <span className="text-apagado">
              {tr(`Página ${pagina} de ${x.totalPaginas}`, `Page ${pagina} of ${x.totalPaginas}`)}
            </span>
            <button
              type="button"
              disabled={pagina >= x.totalPaginas}
              onClick={() => cambiar('pagina', String(pagina + 1))}
              className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
            >
              {tr('Siguiente →', 'Next →')}
            </button>
          </div>
        )}
      </ZonaAmpliable>
    </section>
  );
}
